import React, { useEffect, useState } from 'react';
import { Form, Grid, Button, Header, Dropdown } from 'semantic-ui-react';
import config from '../../../../common/repository/config';
import LocalStorage from '../../../../lib/LocalStorage';
import { toast } from '../../../common/Toast';

interface UpdateTemplateProps {
    onBack: () => void;
    template: {
        id: string;
        name: string;
        status?: string; 
        message: string;
        message_subject?: string;
        type?: string;
        placeholder?: string[];
    };
}

interface TypeOption {
    key: string;
    value: string;
    text: string;
}
interface TypeStatusOption {
    key: string;
    value: string;
    text: string;
}
const UpdateTemplate: React.FC<UpdateTemplateProps> = ({ onBack, template }) => {
    const [name, setName] = useState(template.name || '');
    const [subject, setSubject] = useState(template.message_subject || '');
    const [message, setMessage] = useState(template.message || '');
    const [loading, setLoading] = useState(false);
    const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
    const [selectedType, setSelectedType] = useState<string | null>(template.type || null);
    const [placeholders, setPlaceholders] = useState<{ key: string; text: string; value: string }[]>([]);
    const [selectedPlaceholders, setSelectedPlaceholders] = useState<string[]>(template.placeholder || ['']);
    const [typeStatusOptions, setTypeStatusOptions] = useState<TypeStatusOption[]>([]);
    const [selectedStatus,setSelectedStatus]=useState<any | null>(null);
    const handleAddPlaceholder = () => {
        setSelectedPlaceholders([...selectedPlaceholders, '']);
    };
    const handleSubmit = async () => {
        if (!name.trim() || !message.trim()) {
            toast.error(`Please Enter ${!name.trim() ? 'Name' : 'Message'}`);
            return;
        }

        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const body = {
                id: template?.id,
                name,
                message,
                message_subject: subject,
                type: selectedType,
                status:selectedStatus,
                placeholder: selectedPlaceholders,
            };

            const response = await fetch(`${config.apiBaseURL}/template/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify(body),
            });

            const result = await response.json();

            if (!response.ok) {
                const errorMessage = result?.message || 'Failed to update template.';
                toast.error(errorMessage);
                throw new Error(errorMessage);
            }

            toast.success(result?.message || 'Template updated successfully');
            onBack();
        } catch (error) {

            console.error(error);
        } finally {
            setLoading(false);
        }
    };
    const handleGetType = async () => {
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getTemplate`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            const items = result?.data?.items || {};

            const formattedOptions: TypeOption[] = Object.entries(items).map(([key, value]) => ({
                key,
                value: key,        // ✅ this is correct
                text: value as string,
            }));

            setTypeOptions(formattedOptions);
        } catch (err) {
            toast.error('Failed to fetch types');
            console.error(err);
        }
    };

    const handleGetPlaceholders = async () => {
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getPlaceholder`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            const items = result?.data?.items || {};
            const formattedOptions: any = Object.entries(items).map(([value, label]) => ({
                key: value,
                value: value,
                text: label,
            }));

            setPlaceholders(formattedOptions);
        } catch (err) {
            toast.error('Failed to fetch placeholders');
            console.error(err);
        }
    };
    const handleGetStatus = async () => {
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getTemplateStatus`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });
            const result = await response.json();
            const items = result?.data?.items || {};
            const formattedOptions: TypeStatusOption[] = Object.entries(items).map(([key, value]) => ({
                key,
                value: key,
                text: value as string,
            }));

            setTypeStatusOptions(formattedOptions);
        } catch (err) {
            toast.error('Something went wrong');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleGetType();
        handleGetPlaceholders();
        handleGetStatus();
    }, []);

    useEffect(() => {
        if (typeOptions.length && template.type) {
            const matchedOption = typeOptions.find(opt => opt.text === template.type);
            if (matchedOption) {
                setSelectedType(matchedOption.value);
            }
        }
        if (typeStatusOptions.length && template.status) {
            const matchedOption = typeStatusOptions.find(opt => opt.text === template.status);
            if (matchedOption) {
                setSelectedStatus(matchedOption.value);
            }
        }
        
        
    }, [typeOptions, template.type ,typeStatusOptions,template.status]);
    useEffect(() => {
        if (placeholders.length && template.placeholder) {

            const updatedSelectedPlaceholders = template.placeholder.map((p: string) => {

                const matchedPlaceholder = placeholders.find(ph => ph.text === p);
                return matchedPlaceholder ? matchedPlaceholder.value : p;
            });
            setSelectedPlaceholders(updatedSelectedPlaceholders);
        }
    }, [placeholders, template.placeholder]);
    // handle Chnage
    const handleChange = (index: number, value: string) => {
        const updated = [...selectedPlaceholders];
        updated[index] = value;
        setSelectedPlaceholders(updated);
    };

    const handleDelete = (indexToRemove: number) => {
        setSelectedPlaceholders(prev =>
            prev.filter((_, index) => index !== indexToRemove)
        );
    };


    return (
        <div style={{ padding: '3rem 2rem 2rem' }}>
            <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Header as="h3" dividing style={{ marginBottom: '2rem' }}>
                    Update Template
                </Header>
                <Grid padded="vertically">
                    <Grid.Row columns={2} verticalAlign="middle">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Name<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.Input
                                placeholder='Enter template name'
                                value={name}
                                onChange={(e, data) => setName(data.value as string)}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={2} verticalAlign="middle">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Type</label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            < Form.Dropdown
                                selection
                                options={typeOptions}
                                placeholder="Select Type"
                                value={selectedType as string}
                                onChange={(e, data) => setSelectedType(data.value as string)}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={2} verticalAlign="middle">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Status</label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            < Form.Dropdown
                                selection
                                options={typeStatusOptions}
                                placeholder="Select Status"
                                value={selectedStatus as string}
                                onChange={(e, data) => setSelectedStatus(data.value as string)}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    {selectedPlaceholders.map((selected, index) => (
                        <Grid.Row key={index} verticalAlign="middle">
                            <Grid.Column width={3}>
                                <label style={{ fontWeight: 600 }}>Placeholder {index + 1}</label>
                            </Grid.Column>
                            <Grid.Column width={6}>
                                <Form.Dropdown
                                    placeholder="Select Placeholder"
                                    fluid
                                    selection
                                    options={placeholders}
                                    value={selected}
                                    onChange={(e, { value }) => handleChange(index, value as string)}
                                />
                            </Grid.Column>

                             <Grid.Column width={1}>
                                    <Button
                                        icon="close"
                                        color="red"
                                        size="small"
                                        onClick={() => handleDelete(index)}
                                    />
                             </Grid.Column>
                        </Grid.Row>
                    ))}
                    <Grid.Row>
                        <Grid.Column width={3} />
                        <Grid.Column width={6}>
                            <Button type="button" onClick={handleAddPlaceholder} secondary>
                                Add Placeholder
                            </Button>
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row columns={2} verticalAlign="top">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Subject</label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.TextArea
                                placeholder='Enter Subject'
                                value={subject}
                                onChange={(e, data) => setSubject(data.value as string)}
                                style={{ minHeight: 100 }}
                            />
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row columns={2} verticalAlign="top">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Message<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.TextArea
                                placeholder='Enter message'
                                value={message}
                                onChange={(e, data) => setMessage(data.value as string)}
                                style={{ minHeight: 100 }}
                            />
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                        <Grid.Column width={3} />
                        <Grid.Column width={6}>
                            <Button type='submit' primary loading={loading}>
                                Update
                            </Button>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Form>
        </div>
    );
};

export default UpdateTemplate;
